import { Modal, BodyText, Checkbox, DetailText, CalendarPlusIcon } from 'design-configuration/components'
import { useState } from 'react'
import {
  ExhibitionProjectFragment,
  ExhibitionProjectStatusFragment,
  ProjectCalendarFragment
} from '@/gql/generated/graphql'
import { Button, ButtonProps } from '@components/button'
import { formatDate } from '@utils/common'
import { Dropdown } from '@components/dropdown'
import { cn } from '@/lib/utils'
import { fullProjectPurchaseRoute } from '@utils/routes'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import React from 'react'
import { handleCalendar } from './utils'
import { getExhibitionProjectEvent } from '@/app/exhibitions/[slug]/utils/exhibition-utils'

export enum CalendarOptions {
  google = 'Google Calendar',
  iCal = 'iCal',
  outlook = 'Outlook',
  yahoo = 'Yahoo! Calendar'
}

export type CalendarEvent = {
  startTime: Date
  endTime: Date
  name: string
  displayName?: string
  displaySubheading?: string
  shortName?: string
  description: string
  url?: string
}

export enum TrackedAddToCalButtonNames {
  exhibitionPopUpCTA = 'Exhibition Pop Up CTA',
  exhibitionWorksCard = 'Exhibition Works Card',
  exhibitionWorksDescription = 'Exhibition Works Description',
  homePageExhibitionSlide = 'Home Page Exhibition Slide',
  homePageProjectSlide = 'Home Page Project Slide',
  projectHeader = 'Project Header',
  calendarGridItem = 'Calendar Grid Item'
}

const ONE_HOUR = 60 * 60 * 1000

export interface AddToCalendarButtonProps extends ButtonProps {
  events: CalendarEvent[]
  buttonName: TrackedAddToCalButtonNames
}

export const AddToCalendarButton = React.forwardRef<HTMLButtonElement, AddToCalendarButtonProps>(
  ({ className, iconLeft, iconRight, events, onClick, buttonName, ...props }: AddToCalendarButtonProps, ref) => {
    const sortedEvents = events?.sort((a, b) => a.startTime.getTime() - b.startTime.getTime())
    const [selectedEvents, setSelectedEvents] = useState<CalendarEvent[]>(
      sortedEvents?.length > 1 ? [] : [...(sortedEvents || [])]
    )
    const [show, setShow] = useState<boolean>(false)
    const [calendarApp, setCalendarApp] = useState<CalendarOptions>(CalendarOptions.google)

    const onEventSelection = (event: CalendarEvent) => {
      if (sortedEvents.length === 1) {
        setSelectedEvents([event])
        return
      }
      if (selectedEvents.includes(event)) {
        setSelectedEvents(selectedEvents.filter((e) => e !== event))
      } else {
        setSelectedEvents([...selectedEvents, event])
      }
    }

    return (
      <>
        <Modal
          show={show}
          onDismiss={() => {
            setSelectedEvents([])
            setShow(false)
          }}
          dismissOnClickOutside
          modalType="md"
        >
          <div className="gap-y-md flex h-full w-full flex-col">
            <div className="gap-x-xs flex items-center font-medium">
              <CalendarPlusIcon className="w-md h-md fill-on-surface" />
              <BodyText>Add a reminder to your calendar</BodyText>
            </div>
            <div className="border-outline-variant gap-y-sm bg-surface-low p-sm flex max-h-[250px] flex-col overflow-auto rounded-sm border md:max-h-[400px]">
              {sortedEvents?.map((event) => {
                return (
                  <EventButton
                    key={event.name}
                    event={event}
                    onClick={() => onEventSelection(event)}
                    selected={selectedEvents.includes(event)}
                    multi={sortedEvents.length > 1}
                  />
                )
              })}
            </div>
            <div className="gap-x-xs gap-y-xs flex w-full flex-wrap items-center justify-between">
              <BodyText>Calendar preference:</BodyText>
              <Dropdown
                options={Object.values(CalendarOptions)}
                selected={calendarApp}
                setSelected={(option: string) => {
                  setCalendarApp(option as CalendarOptions)
                }}
                className="w-full sm:w-auto"
              />
            </div>
            <div>
              {sortedEvents.length > 1 ? (
                <DetailText className="text-on-surface-variant mb-xs text-center">
                  Allow pop-ups in your browser to add more than one event
                </DetailText>
              ) : null}
              <a onClick={() => handleCalendar(calendarApp, selectedEvents)}>
                <Button
                  onClick={() => {
                    trackButtonClicked(TrackedButtonNames.addToCalendarConfirmed, {
                      customProperties: {
                        Button: buttonName,
                        Calendar: calendarApp,
                        Events: selectedEvents.map((event) => event.name)
                      }
                    })
                    setShow(false)
                    setSelectedEvents([])
                  }}
                  className="w-full"
                  disabled={selectedEvents.length === 0}
                >
                  Confirm
                </Button>
              </a>
            </div>
          </div>
        </Modal>
        <Button
          onClick={(event) => {
            trackButtonClicked(TrackedButtonNames.addToCalendarOpened, {
              customProperties: {
                Button: buttonName,
                Events: events?.map((event) => event?.name)
              }
            })
            setShow(true)
            setSelectedEvents(sortedEvents?.length > 1 ? [] : [...sortedEvents])
            onClick?.(event)
          }}
          className={className}
          ref={ref}
          {...props}
        >
          {iconLeft ? <span>{iconLeft}</span> : null}
          <span className="font-[inherit] tracking-[inherit]">{props.children}</span>
          {iconRight ? <span>{iconRight}</span> : null}
        </Button>
      </>
    )
  }
)
AddToCalendarButton.displayName = 'AddToCalButton'

type ExhibitionProjectAddToCalendarButtonProps = Omit<ProjectAddToCalendarButtonProps, 'projects'> & {
  exhibition: ExhibitionProjectStatusFragment
  projects: ExhibitionProjectFragment[]
}

export const ExhibitionProjectAddToCalendarButton = React.forwardRef<
  HTMLButtonElement,
  ExhibitionProjectAddToCalendarButtonProps
>(({ exhibition, projects, ...props }: ExhibitionProjectAddToCalendarButtonProps, ref) => {
  const events = projects?.map((project) => getExhibitionProjectEvent(exhibition, project))
  return <AddToCalendarButton {...props} ref={ref} events={events} />
})
ExhibitionProjectAddToCalendarButton.displayName = 'ExhibitionProjectAddToCalButton'

export interface ProjectAddToCalendarButtonProps extends ButtonProps {
  projects: ProjectCalendarFragment[]
  buttonName: TrackedAddToCalButtonNames
}

export const ProjectAddToCalendarButton = React.forwardRef<HTMLButtonElement, ProjectAddToCalendarButtonProps>(
  ({ projects, ...props }: ProjectAddToCalendarButtonProps, ref) => {
    const events = projects?.map((project) => getProjectEvent(project))
    return <AddToCalendarButton {...props} ref={ref} events={events} />
  }
)
ProjectAddToCalendarButton.displayName = 'ProjectAddToCalButton'

export const getProjectEvent = (project: ProjectCalendarFragment): CalendarEvent => {
  const url = fullProjectPurchaseRoute(project.slug)
  const name = `${project.name} by ${project.artist_name} on Art Blocks`
  const displayName = `${project.name}`
  const displaySubheading = `${project.artist_name}`
  const description = `${url}\n${project.auction_end_time ? `\nEstimated end time: ${formatDate(new Date(project.auction_end_time))}\n` : ''}\n${project.description}`
  const startTime = new Date(project.auction_start_time ?? project.start_datetime ?? 0)
  // If auction end time is not set, default to 1 hour after start time
  const endTime = new Date(project.auction_end_time ?? startTime.getTime() + ONE_HOUR)
  return {
    startTime,
    endTime,
    name,
    description,
    url,
    displayName,
    displaySubheading
  }
}

export const createEvent = ({
  name,
  displayName,
  displaySubheading,
  startTime,
  endTime,
  url,
  description
}: {
  name: string
  displayName: string
  displaySubheading?: string
  startTime: Date
  endTime?: Date
  url?: string
  description: string
}): CalendarEvent => {
  const end = endTime ?? new Date(startTime.getTime() + ONE_HOUR)
  return {
    name,
    startTime,
    endTime: end,
    description,
    url,
    displayName,
    displaySubheading
  }
}

const EventButton = ({
  event,
  onClick,
  selected,
  multi
}: {
  event: CalendarEvent
  onClick: () => void
  selected: boolean
  multi: boolean
}) => {
  const startTime = event.startTime
  return (
    <button
      className={cn(
        'p-sm bg-on-primary flex w-full cursor-pointer items-center justify-between rounded-sm border text-left',
        selected && multi ? 'border-outline' : 'border-outline-variant'
      )}
      onClick={onClick}
    >
      <div>
        <BodyText className="font-medium">{event.displayName ?? event.name}</BodyText>
        {event.displaySubheading ? <BodyText>{event.displaySubheading}</BodyText> : null}
        <DetailText className="text-on-surface-variant mt-xs font-medium">{formatDate(startTime)}</DetailText>
      </div>
      {multi ? <Checkbox checked={selected} /> : null}
    </button>
  )
}
